"use client";

import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Eye,
  EyeOff,
  Trash,
  ChevronDown,
  ChevronUp,
  ImageIcon,
  GripVertical,
  Edit3,
  Percent,
} from "lucide-react";
import type { OffersOutput } from "@/interfaces/offers";
import { numberToMoney } from "@/util/moneyConversion";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface OfferItemProps {
  offer: OffersOutput;
  onToggleVisibility: (offerId: string) => void;
  onDelete: (offerId: string) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}

export function OfferItem({
  offer,
  onToggleVisibility,
  onDelete,
  onMoveUp,
  onMoveDown,
}: OfferItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Verifica se a descrição é longa (sem tags HTML)
  const isDescriptionLong = () => {
    if (!offer.description) return false;
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = offer.description;
    const textContent = tempDiv.textContent || tempDiv.innerText || "";
    return textContent.length > 100;
  };

  // Calcular desconto se houver preço original
  const calculateDiscount = () => {
    if (!offer.products || offer.products.length === 0 || !offer.unitPrice)
      return null;

    const originalPrice = offer.products.reduce(
      (sum, product) =>
        sum + (product.unitPrice || 0) * (product.quantity || 1),
      0
    );

    if (originalPrice > offer.unitPrice && originalPrice > 0) {
      const discountPercentage = Math.round(
        ((originalPrice - offer.unitPrice) / originalPrice) * 100
      );
      return { originalPrice, discountPercentage };
    }

    return null;
  };

  const isLong = isDescriptionLong();
  const discount = calculateDiscount();

  return (
    <Card
      className={`group overflow-hidden transition-all duration-200 hover:shadow-md border-l-4 ${
        !offer.isVisible
          ? "opacity-60 border-l-gray-300"
          : "border-l-[#EE7B11] hover:border-l-[#d06a0a]"
      } rounded-lg`}
    >
      {/* Área de arraste (mobile: sempre visível; desktop: aparece no hover) */}
      <div className="flex items-center justify-between p-2 bg-gray-50/50 border-b md:opacity-0 md:group-hover:opacity-100 transition-opacity">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <GripVertical className="h-4 w-4 cursor-grab active:cursor-grabbing" />
          <span>Arraste para reordenar</span>
        </div>
        <div className="flex items-center gap-1">
          {onMoveUp && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-gray-600 hover:text-[#EE7B11]"
              onClick={onMoveUp}
            >
              <ChevronUp className="h-3 w-3" />
            </Button>
          )}
          {onMoveDown && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-gray-600 hover:text-[#EE7B11]"
              onClick={onMoveDown}
            >
              <ChevronDown className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>

      {/* Imagem padronizada */}
      <div className="relative">
        <div className="aspect-video w-full bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
          {offer.products[0]?.file?.url ? (
            <img
              src={offer.products[0].file.url}
              alt={offer.products[0].name}
              className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="w-12 h-12 text-gray-400" />
            </div>
          )}
        </div>

        {/* Badge de desconto */}
        {discount && (
          <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
            <Percent className="h-3 w-3" />
            {discount.discountPercentage}% OFF
          </div>
        )}

        {/* Badge de visibilidade */}
        <div className="absolute top-2 left-2">
          <Badge
            variant={offer.isVisible ? "default" : "secondary"}
            className={`text-xs ${
              offer.isVisible
                ? "bg-green-100 text-green-800 border-green-200"
                : "bg-gray-100 text-gray-600 border-gray-200"
            }`}
          >
            {offer.isVisible ? "Visível" : "Oculto"}
          </Badge>
        </div>
      </div>

      {/* Conteúdo do card */}
      <div className="p-4">
        {/* Nome e edição inline (mobile: sempre visível; desktop: no hover) */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 text-base leading-tight line-clamp-2 group-hover:text-[#EE7B11] transition-colors">
              {offer.name}
            </h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 md:opacity-0 md:group-hover:opacity-100 transition-opacity text-gray-600 hover:text-[#EE7B11]"
            aria-label="Editar"
          >
            <Edit3 className="h-3 w-3" />
          </Button>
        </div>

        {/* Preços */}
        <div className="flex items-center gap-2 mb-3">
          {discount ? (
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-[#EE7B11]">
                {numberToMoney(offer.unitPrice)}
              </span>
              <span className="text-sm text-gray-500 line-through">
                {numberToMoney(discount.originalPrice)}
              </span>
            </div>
          ) : (
            <span className="text-lg font-bold text-gray-900">
              {numberToMoney(offer.unitPrice)}
            </span>
          )}
        </div>

        {/* Descrição */}
        {offer.description && (
          <div className="mb-4">
            <div
              className={`text-sm text-gray-600 leading-relaxed ${
                !isExpanded && isLong ? "line-clamp-2" : ""
              }`}
              dangerouslySetInnerHTML={{ __html: offer.description }}
            />
            {isLong && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-[#EE7B11] hover:text-[#d06a0a] p-0 h-auto mt-1"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? "Mostrar menos" : "Mostrar mais"}
              </Button>
            )}
          </div>
        )}

        {/* Ações */}
        <div className="flex items-center justify-end gap-1 pt-3 border-t border-gray-100">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-gray-600 hover:text-[#EE7B11]"
                  onClick={() => onToggleVisibility(offer.id)}
                  aria-label={
                    offer.isVisible ? "Ocultar produto" : "Mostrar produto"
                  }
                >
                  {offer.isVisible ? (
                    <Eye className="h-4 w-4" />
                  ) : (
                    <EyeOff className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {offer.isVisible ? "Ocultar produto" : "Mostrar produto"}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => onDelete(offer.id)}
                  aria-label="Excluir produto"
                >
                  <Trash className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Excluir produto</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </Card>
  );
}
